// ---------------------------------------------
// AuthReducer.js  (bootstrap /api/v1/me, rutas públicas incl. '/')
// Limpio de logs
// ---------------------------------------------

import { BASE_URL } from '../../setupAxios';
import { decodeToken } from '../../utils/TokenUtils';
import axios from 'axios';

// ---------------------------------------------
// Estado inicial
// ---------------------------------------------
const initialState = {
	ready: false,
	isLoading: false,
	isSuccess: false,
	isError: false,
	message: null,
	user: null,
	token: null,
};

// ---------------------------------------------
// Tipos
// ---------------------------------------------
const AUTH_BOOT_START = 'auth/BOOT_START';
const AUTH_BOOT_SUCCESS = 'auth/BOOT_SUCCESS';
const AUTH_BOOT_DONE = 'auth/BOOT_DONE';
const AUTH_LOGIN_OK = 'auth/LOGIN_OK';
const AUTH_LOGOUT_OK = 'auth/LOGOUT_OK';
const AUTH_ERROR = 'auth/ERROR';

// ---------------------------------------------
// Reducer
// ---------------------------------------------
export default function authReducer(state = initialState, action) {
	switch (action.type) {
		case AUTH_BOOT_START:
			return { ...state, isLoading: true, isError: false, message: null };

		case AUTH_BOOT_SUCCESS:
			return {
				...state,
				isLoading: false,
				ready: true,
				user: pickUser(action.payload),
				token: action.payload?.token ?? state.token ?? null,
				isSuccess: true,
				isError: false,
				message: null,
			};

		case AUTH_BOOT_DONE:
			return {
				...state,
				isLoading: false,
				ready: true,
				user: null,
				token: null,
				isSuccess: false,
			};

		case AUTH_LOGIN_OK:
			return {
				...state,
				isLoading: false,
				ready: true,
				user: pickUser(action.payload),
				token: action.payload?.token ?? null,
				isSuccess: true,
				isError: false,
				message: null,
			};

		case AUTH_LOGOUT_OK:
			return {
				...state,
				isLoading: false,
				ready: true,
				user: null,
				token: null,
				isSuccess: false,
				isError: false,
				message: null,
			};

		case AUTH_ERROR:
			return {
				...state,
				isLoading: false,
				isError: true,
				isSuccess: false,
				message: action.payload ?? null,
			};

		default:
			return state;
	}
}

const baseURL = BASE_URL;

// ---------------------------------------------
// Endpoints normalizados (backend montado en /auth/*)
// ---------------------------------------------
const ENDPOINTS = {
	ME: '/api/v1/me',
	LOGIN: '/api/v1/login',
	LOGOUT: '/api/v1/logout',
};

// Rutas públicas donde NO lanzamos bootstrap
const PUBLIC_PATHS_RE = /^\/($|login|signin|forgot|reset)/i;

// ---------------------------------------------
// Helpers
// ---------------------------------------------
function pickUser(obj) {
	if (!obj) return null;
	if (obj.user) return obj.user;
	if (obj.data?.user) return obj.data.user;
	if (obj.data) return obj.data;
	return obj;
}

function isNetworkError(err) {
	if (!err) return false;
	const msg = String(err.message || err);
	return (
		err.name === 'TypeError' ||
		/NetworkError|Failed to fetch|ERR_CONNECTION|ECONNREFUSED|ENOTFOUND/i.test(
			msg,
		)
	);
}

async function fetchJSON(url, opts) {
	const headers = { Accept: 'application/json', ...(opts.headers || {}) };
	try {
		const t = localStorage.getItem('isAuthenticated') === 'true';
		
	} catch {}

	const res = await fetch(url, {
		credentials: 'include',
		headers,
		...opts,
	});

	return res;
}

// util espera breve
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// ---------------------------------------------
// Thunks
// ---------------------------------------------
/**
 * Bootstrap de autenticación:
 * - No se ejecuta en rutas públicas (/, /login, etc.).
 * - Intenta GET /api/v1/me; si 2xx, inicializa sesión; si no, marca listo sin usuario.
 */
export const bootstrapAuth = () => async (dispatch, getState) => {
	if (getState()?.auth?.ready) return;

	dispatch({ type: AUTH_BOOT_START });

	const path = window.location?.pathname || '';
	const hasJwt = localStorage.getItem('isAuthenticated') === 'true';
	if (PUBLIC_PATHS_RE.test(path) && !hasJwt) {
		dispatch({ type: AUTH_BOOT_DONE });
		return;
	}

	try {
		const res = await fetchJSON(`${baseURL}${ENDPOINTS.ME}`, {
			method: 'GET',
		});
		if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
		const data = await res.json().catch(() => ({}));

		const inlineUser = data?.user ?? data?.data?.user ?? (data?.data?.userId ? data.data : null);
		const encoded = data?.token ?? data?.data?.token ?? null;
		const userObj =
			inlineUser ||
			(encoded
				? (() => {
						try {
							return decodeToken(encoded);
						} catch {
							return null;
						}
					})()
				: null);

		const payload = userObj ? { user: userObj } : data;
		dispatch(loginOk(payload));
		return payload;
	} catch {
		dispatch(logoutOk());
		return null;
	}
};

export const loginOk = payload => ({ type: AUTH_LOGIN_OK, payload });
export const logoutOk = () => ({ type: AUTH_LOGOUT_OK });

/**
 * Obtiene el usuario actual (GET /api/v1/me).
 * - En pantallas públicas evita la llamada salvo que se fuerce (p.ej., tras login).
 */
export const getMe =
	(opts) =>
	async dispatch => {
		const path = window.location?.pathname || '';
		const hasJwt = localStorage.getItem('isAuthenticated') === 'true';
		const isPublic = PUBLIC_PATHS_RE.test(path);

		if (isPublic && !hasJwt && !opts.force) return null;

		try {
			const res = await fetchJSON(`${baseURL}${ENDPOINTS.ME}`, {
				method: 'GET',
			});
			if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
			const data = await res.json().catch(() => ({}));
			dispatch(loginOk(data));
			return data;
		} catch {
			dispatch(logoutOk());
			return null;
		}
	};

/**
 * Login (POST /api/v1/login) + getMe forzado con retry corto.
 */
export const loginUser =
	({ email, password }) =>
	async dispatch => {
		try {
			const res = await fetchJSON(`${baseURL}${ENDPOINTS.LOGIN}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email, password }),
			});
			if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

			const body = await res.json().catch(() => null);

			// Persistir token si viene en el body
			try {
				const token =
					body?.token ??
					body?.data?.token ??
					(typeof body?.data === 'string' ? body.data : null);

				if (token) {
					try {
						localStorage.setItem('isAuthenticated', 'true');
					} catch {}
					try {
						axios.defaults.headers.common.Authorization = `Bearer ${token}`;
					} catch {}
					const me = await dispatch(
						getMe({ force: true, _attempt: 'after-login-token' }),
					);
					if (me) return me;
				}
			} catch {}

			// 1) Si el back ya devuelve el usuario en claro, úsalo (flujo plano o anidado)
			const inlineUser = body?.user ?? body?.data?.user ?? (body?.data?.userId ? body.data : null);

			// 2) Si devuelve un token con el usuario codificado, decodifícalo
			const encoded =
				body?.token ??
				body?.data?.token ??
				(typeof body?.data === 'string' ? body.data : null);
			const decodedUser =
				!inlineUser && encoded
					? (() => {
							try {
								return decodeToken(encoded);
							} catch {
								return null;
							}
						})()
					: null;

			if (inlineUser || decodedUser) {
				const payload = { user: inlineUser || decodedUser };
				dispatch(loginOk(payload));
				return payload;
			}

			// 3) Fallback: si no vino user, esperamos un pelín y probamos /api/v1/me (cookie)
			const max = 8;
			const delay = 300;
			await sleep(delay);
			for (let i = 0; i < max; i++) {
				const me = await dispatch(
					getMe({ force: true, _attempt: i + 1 }),
				);
				if (me) return me;
				await sleep(delay);
			}

			dispatch({
				type: AUTH_ERROR,
				payload:
					'No se pudo establecer la sesión (cloud). Inténtalo de nuevo.',
			});
			return null;
		} catch (e) {
			if (isNetworkError(e)) {
				dispatch({
					type: AUTH_ERROR,
					payload: `No se puede conectar con la API (${baseURL}). Verifica que el backend esté levantado y CORS permita credenciales.`,
				});
			} else {
				dispatch({
					type: AUTH_ERROR,
					payload: 'Email o contraseña incorrectos.',
				});
			}
			dispatch(logoutOk());
			return null;
		}
	};

/**
 * Logout (DELETE /api/v1/logout) – ignora 404/red.
 */
export const logout = () => async dispatch => {
	try {
		await fetchJSON(`${baseURL}${ENDPOINTS.LOGOUT}`, { method: 'DELETE' });
	} catch {
	} finally {
		try {
			localStorage.removeItem('isAuthenticated');
		} catch {}
		dispatch(logoutOk());
	}
};

/**
 * Limpia flags y usuario.
 */
export const reset = () => dispatch => {
	dispatch(logoutOk());
};
